import { IMenuItem } from "@/types/types";
import { cloneDeep } from "lodash";
import React, { useEffect, useState } from "react";
import { appHistory } from "@ice/stark-app";
import { useAppRouteContext } from "@/contexts";
import { Menu } from "antd";
import { keepSystemParams } from "@/hooks";
import {
  isLowcodePage,
  traverseMenuTree,
  findMenuTreeNode,
  findMenuTreeNodeAndParents,
  getEffectivePagePath,
  filterUnVisibleNodes,
  postMessageToWorkbench,
} from "@/utils";
import { getMenus } from "@/services";
import { AntdIcon } from "@/components";
import styles from "./index.module.css";

/**
 * 按照appHistory方法扩写, 支持带入当前url中的search部分
 * （主要用于ui_hide_nav等url控制参数）
 */
const appHistoryWithParams = {
  push: (path: string) => {
    appHistory.push(keepSystemParams(path));
  },
  replace: (path: string) => {
    appHistory.replace(keepSystemParams(path));
  },
};

export default function Menus({ collapsed }: { collapsed: boolean }) {
  const { currentPath, onePageBasePath } = useAppRouteContext(); // 当前路径

  const pathname = getEffectivePagePath(currentPath); // 根据当前路径获取有效的页面pathname
  const basePath = onePageBasePath ? getEffectivePagePath(onePageBasePath) : ""; // 根据当前路径获取有效的页面pathname

  const [selectedMenuKey, setSelectedMenuKey] = useState<string>(
    basePath || pathname,
  ); // 选中菜单项, 受控
  const [openedKeys, setOpenedKeys] = useState<string[]>(null); // 展开的菜单项, 受控
  const [showMenu, setShowMenu] = useState<IMenuItem>({}); // 用于antd最终渲染的menu数据
  const [indexPath, setIndexPath] = useState<string>(null); // 默认首页path

  // 处理菜单项点击事件
  const handleMenuClick = (e: { key: string }) => {
    const matchedRoute = findMenuTreeNode(showMenu, e.key);
    if (!matchedRoute) return; // 如果没有找到匹配的路由，直接返回
    const { key = "", type, url } = matchedRoute;
    if (isLowcodePage(type)) {
      // 低码页面
      setSelectedMenuKey(key);
      appHistoryWithParams.push(key);
    } else if (type === "procode") {
      // subApp页面
      setSelectedMenuKey(key);
      appHistoryWithParams.push(key);
    } else if (type === "iframe") {
      // iframe页面
      setSelectedMenuKey(key);
      appHistoryWithParams.push(key);
    } else if (type === "link" && url) {
      // 外链
      window.open(url, "_blank");
    } else if (type === "folder") {
      // 文件夹 什么都不做
    } else {
      setSelectedMenuKey(key);
      appHistoryWithParams.push(key);
    }
    // 调用postMessage方法, 将内部的菜单切换变化通知到工作台
    postMessageToWorkbench({
      action: "menuSelected",
      data: key,
    });
  };

  // 从 API 获取动态的菜单数据, 处理父节点选中, 补齐menu渲染需要的数据
  useEffect(() => {
    const fetchMenu = async () => {
      const { menus } = await getMenus();
      const menuData = cloneDeep(menus); // menuData仅用于菜单展现, 需要先深拷贝一下, 避免原始menus数据变化
      const visibleMenuData = filterUnVisibleNodes(menuData); // 过滤掉不可见节点

      // TODO: 预览模式下需要过滤掉react类型的页面菜单

      let findIndexPath; // 设置默认首页path
      // 遍历处理, 找默认首页, 补 menu icon 等
      traverseMenuTree(visibleMenuData, (node) => {
        // 1. 按深度遍历的顺序找最第一个菜单项，folder, link 的情况不做默认首页处理
        // TODO: 用户通过应用配置指定默认首页的情况
        if (!findIndexPath && node.type !== "folder" && node.type !== "link") {
          findIndexPath = node.key;
        }
        // 2. 给folder菜单补默认icon
        let icon = node.extend?.icon;
        if (!icon && node.type === "folder") {
          icon = "MenuOutlined";
        }
        node.icon = icon ? <AntdIcon type={icon} /> : null;
        // 3. 仅保留Menu渲染需要的字段
        Object.keys(node).forEach((key) => {
          if (
            !["icon", "key", "label", "type", "url", "children"].includes(key)
          ) {
            delete node[key];
          }
        });
      });
      setShowMenu(visibleMenuData);
      setIndexPath(findIndexPath);
    };
    fetchMenu();
  }, []);

  // 处理初始化的菜单选中和展开
  useEffect(() => {
    // 根据当前选中的子菜单项, 找到父节点, 设置默认展开菜单项
    const parseAndSetDefaultOpendKey = (menuData, selectedMenuKey) => {
      const result = findMenuTreeNodeAndParents(menuData, selectedMenuKey); // 获取当前选中节点的所有父节点
      if (result) {
        setOpenedKeys(result.parents.map(({ key }) => key));
      }
    };

    // 如果当前访问的是根域名 , 需要 redirect 到默认首页
    if (pathname === "" && indexPath) {
      appHistoryWithParams.replace(indexPath); // replace跳转
      setSelectedMenuKey(indexPath); // 选中菜单项
      parseAndSetDefaultOpendKey(showMenu, indexPath); // 设置默认展开菜单
      return;
    }

    // 如果openedKeys已经有值了, 说明已经是用户主动点击展开收起的操作了, 无需继续处理
    if (openedKeys) return;

    setSelectedMenuKey(basePath || pathname);
    parseAndSetDefaultOpendKey(showMenu, basePath || pathname); // 设置默认展开菜单
  }, [showMenu, indexPath, pathname, basePath]);

  return (
    <Menu
      className={styles.menu}
      selectedKeys={[selectedMenuKey]}
      onClick={handleMenuClick}
      openKeys={openedKeys}
      onOpenChange={(openKeys) => setOpenedKeys(openKeys)}
      inlineIndent={24}
      mode="inline"
      theme="light"
      inlineCollapsed={collapsed}
      items={showMenu.children as any}
    />
  );
}
